import React, { useState, useEffect, useMemo } from 'react';
import { Input, notification, Spin, Pagination, InputNumber, Modal } from 'antd';
import { Button as CrceButton } from '@crc-evans/ui-components';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Loader2, Save, Boxes } from 'lucide-react';
import moment from 'moment';
import { connect } from 'react-redux';
import { fetchConsumablesLog, updateConsumablesLog, updateDsrLogSections } from '../../redux/dsr/action';
import './TechnicianLogSection.css';
import './ConsumablesSection.css';

const { TextArea } = Input;

const ConsumablesSection = ({
  shift,
  selectedDate,
  selectedProject,
  selectedMainline,
  currentDsrLog,
  isSkipped,
  isSubmitted,
  isShiftSkipped = false,
  onComplete,
  onCancel,
  onSkipChange,
  onFetchConsumablesLog,
  onUpdateConsumablesLog,
  onUpdateDsrLogSections,
  isConsumablesLogLoading,
  isUpdatingConsumablesLog,
  currentConsumablesLog,
}) => {
  const [rowData, setRowData] = useState([]);
  const [comments, setComments] = useState('');
  const [skipped, setSkipped] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [paginationState, setPaginationState] = useState({ current: 1, pageSize: 10 });
  const [hoveredRowId, setHoveredRowId] = useState(null);

  const getShiftNumber = (shiftStr) => (shiftStr === 'day' ? 0 : 1);

  /** API: { message, data: [...] } — legacy shapes used data.data nesting. */
  const normalizeConsumablesList = (apiData) => {
    if (!apiData) return [];
    if (Array.isArray(apiData)) return apiData;
    if (Array.isArray(apiData.data)) return apiData.data;
    if (Array.isArray(apiData.data?.data)) return apiData.data.data;
    return [];
  };

  const convertApiDataToTableFormat = (apiData) => {
    const list = normalizeConsumablesList(apiData);
    if (!list.length) return [];

    return list.map((item) => ({
      id: item.id,
      projectConsumableId: item.projectConsumableId,
      category: item.category || '-',
      name: item.consumableName || '-',
      productCode: item.productCode || '-',
      quantityAvailable: item.pendingQuantityInStock || 0,
      unit: item.unit || '-',
      quantityUsed: item.quantityUsed || 0,
      additionalRequired: item.additionalRequired || 0,
      apiData: item,
    }));
  };

  useEffect(() => {
    if (selectedProject && selectedMainline && selectedDate) {
      const userEmail = JSON.parse(localStorage.getItem('user')).email;
      const logDate = moment(selectedDate).format('YYYY-MM-DD');
      const shiftNumber = getShiftNumber(shift);

      onFetchConsumablesLog({
        projectId: selectedProject.id,
        logDate: logDate,
        userEmail: userEmail,
        shift: shiftNumber,
        sowId: selectedMainline.id,
      });
    }
  }, [selectedProject, selectedMainline, selectedDate, shift, onFetchConsumablesLog]);

  useEffect(() => {
    if (currentConsumablesLog == null) return;
    const convertedData = convertApiDataToTableFormat(currentConsumablesLog);
    setRowData(convertedData);
    setIsDirty(false);
  }, [currentConsumablesLog]);

  useEffect(() => {
    setSkipped(isSkipped || false);
  }, [isSkipped]);

  useEffect(() => {
    setIsDirty(false);
  }, [selectedDate]);

  useEffect(() => {
    setSkipped(isSkipped || false);
  }, [isSkipped, shift, selectedDate]);

  useEffect(() => {
    return () => {
      setRowData([]);
      setComments('');
      setSkipped(false);
      setIsDirty(false);
    };
  }, []);

  const canEditQuantityUsed = (row) => {
    const stock = Number(row?.quantityAvailable ?? 0);
    return !skipped && !Number.isNaN(stock) && stock > 0;
  };

  const canEditAdditional = () => !skipped;

  const handleQuantityUsedChange = (row, val) => {
    const stock = Number(row.quantityAvailable ?? 0);
    if (stock <= 0) {
      notification.warning({
        message: 'Cannot enter used quantity',
        description: 'Used quantity in shift is not allowed when consumable stock is zero.',
        placement: 'bottomRight',
      });
      setRowData((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, quantityUsed: 0 } : r))
      );
      setIsDirty(true);
      return;
    }
    const used = val == null ? 0 : val;
    setRowData((prev) =>
      prev.map((r) => (r.id === row.id ? { ...r, quantityUsed: used } : r))
    );
    setIsDirty(true);
  };

  const handleAdditionalRequiredChange = (row, val) => {
    const n = val == null ? 0 : val;
    setRowData((prev) =>
      prev.map((r) => (r.id === row.id ? { ...r, additionalRequired: n } : r))
    );
    setIsDirty(true);
  };

  const totalRows = rowData.length;
  const { current: currentPage, pageSize } = paginationState;
  const maxPage = Math.max(1, totalRows === 0 ? 1 : Math.ceil(totalRows / pageSize));
  const page = Math.min(currentPage, maxPage);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return rowData.slice(start, start + pageSize);
  }, [rowData, page, pageSize]);

  useEffect(() => {
    if (currentPage > maxPage) {
      setPaginationState((s) => ({ ...s, current: maxPage }));
    }
  }, [currentPage, maxPage]);

  const handleCommentsChange = (e) => {
    setComments(e.target.value);
    setIsDirty(true);
  };

  const handleSkipChange = (e) => {
    const newSkipState = e.target.checked;
    setSkipped(newSkipState);
    setIsDirty(true);

    if (onSkipChange) {
      onSkipChange(e);
    } else if (currentDsrLog?.id) {
      const submissionStatus = generateSubmissionStatus('consumables', shift, true);
      const sectionsSkipped = {
        ...currentDsrLog.sectionsSkipped,
        isConsumablesSkippedDayShift: shift === 'day' ? (newSkipState ? 1 : 0) : (currentDsrLog.sectionsSkipped?.isConsumablesSkippedDayShift || 0),
        isConsumablesSkippedNightShift: shift === 'night' ? (newSkipState ? 1 : 0) : (currentDsrLog.sectionsSkipped?.isConsumablesSkippedNightShift || 0),
      };

      onUpdateDsrLogSections({
        dsrLogId: currentDsrLog.id,
        submissionStatus: submissionStatus,
        sectionsSkipped: sectionsSkipped,
      });
    }
  };

  const generateSubmissionStatus = (section, sh, submittedFlag = true) => {
    const shiftKeySuffix = sh === 'day' ? 'Dayshift' : 'Nightshift';
    let submittedKey;

    switch (section) {
      case 'technician':
        submittedKey = `isTechnicianLogSubmitted${shiftKeySuffix}`;
        break;
      case 'downtime':
        submittedKey = `isDowntimeSubmitted${shiftKeySuffix}`;
        break;
      case 'consumables':
        submittedKey = `isConsumablesSubmitted${shiftKeySuffix}`;
        break;
      case 'equipment':
        submittedKey = `isEquipmentUsageSubmitted${shiftKeySuffix}`;
        break;
      case 'ehs':
        submittedKey = `isEhsIssuesSubmitted${shiftKeySuffix}`;
        break;
      case 'material':
        submittedKey = `isMaterialUsageSubmitted${shiftKeySuffix}`;
        break;
      case 'production':
        submittedKey = `isProductionJointSubmitted${shiftKeySuffix}`;
        break;
      default:
        return {};
    }

    return { [submittedKey]: submittedFlag };
  };

  const handleSave = async () => {
    if (skipped) {
      if (onComplete) onComplete(true, 'consumables');
      return;
    }

    try {
      const dsrLogConsumables = rowData.map((row) => ({
        dsrLogId: currentDsrLog?.id,
        projectConsumableId: row.projectConsumableId,
        quantityUsed: row.quantityUsed || 0,
        additionalRequired: row.additionalRequired || 0,
        shift: getShiftNumber(shift),
      }));

      const payload = {
        dsrLogConsumables,
      };

      await onUpdateConsumablesLog(payload);
      setIsDirty(false);

      const submissionStatus = generateSubmissionStatus('consumables', shift, true);
      const sectionsSkipped = {
        ...currentDsrLog.sectionsSkipped,
        isConsumablesSkippedDayShift: shift === 'day' ? 0 : (currentDsrLog.sectionsSkipped?.isConsumablesSkippedDayShift || 0),
        isConsumablesSkippedNightShift: shift === 'night' ? 0 : (currentDsrLog.sectionsSkipped?.isConsumablesSkippedNightShift || 0),
      };

      await onUpdateDsrLogSections({
        dsrLogId: currentDsrLog?.id,
        submissionStatus: submissionStatus,
        sectionsSkipped: sectionsSkipped,
      });

      if (onComplete) onComplete(true, 'consumables');

      if (selectedProject && selectedMainline && selectedDate) {
        const userEmail = JSON.parse(localStorage.getItem('user')).email;
        const logDate = moment(selectedDate).format('YYYY-MM-DD');
        const shiftNumber = shift === 'day' ? '0' : '1';

        onFetchConsumablesLog({
          projectId: selectedProject.id,
          logDate: logDate,
          userEmail: userEmail,
          shift: shiftNumber,
          sowId: selectedMainline.id,
        });
      }
    } catch (error) {
      console.error('Error saving consumables log:', error);
      notification.error({
        message: 'Save Failed',
        description: 'Failed to save consumables data. Please try again.',
      });
    }
  };

  const handleNext = () => {
    if (isDirty) {
      Modal.confirm({
        title: 'Warning!',
        icon: <ExclamationCircleOutlined />,
        content: 'You have made changes to the table. Do you want to save them?',
        okText: 'Save',
        cancelText: 'Discard',
        onOk: () => {
          handleSave();
          if (onComplete) onComplete(!skipped);
        },
        onCancel: () => {
          if (onComplete) onComplete(!skipped);
        },
      });
    } else {
      if (onComplete) onComplete(!skipped);
    }
  };

  return (
    <div className="modern-dsr-tech-log-section modern-dsr-consumables-usage">
      <Spin
        spinning={isConsumablesLogLoading}
        indicator={(
          <span className="modern-dsr-tech-log-spin" aria-hidden>
            <Loader2 className="modern-dsr-tech-log-spin-icon" size={28} strokeWidth={2} />
          </span>
        )}
        tip="Loading consumables data..."
        size="large"
      >
        <div className="modern-dsr-tech-log-card">
            <div className="modern-dsr-tech-log-card-head">
              <div className="modern-dsr-tech-log-card-title-block">
                <div className="modern-dsr-tech-log-card-icon" aria-hidden>
                  <Boxes size={22} strokeWidth={2} />
                </div>
                <div>
                  <h3 className="modern-dsr-tech-log-card-title">Consumables and PPE usage</h3>
                  <p className="modern-dsr-tech-log-card-sub">Record quantity used and additional required per line for this shift.</p>
                </div>
              </div>
            </div>

            <div className="modern-dsr-tech-table" role="table" aria-label="Consumables and PPE">
              <div
                className="modern-dsr-tech-thead modern-dsr-cs-grid"
                role="row"
              >
                <div className="modern-dsr-tech-th modern-dsr-cs-th--tight" role="columnheader">Consumable category</div>
                <div className="modern-dsr-tech-th modern-dsr-cs-th--tight" role="columnheader">Name</div>
                <div className="modern-dsr-tech-th modern-dsr-cs-th--tight" role="columnheader">Product code</div>
                <div className="modern-dsr-tech-th modern-dsr-tech-th--center modern-dsr-cs-th--tight" role="columnheader">Quantity available</div>
                <div className="modern-dsr-tech-th modern-dsr-tech-th--center modern-dsr-cs-th--tight" role="columnheader">Unit</div>
                <div className="modern-dsr-tech-th modern-dsr-tech-th--center modern-dsr-cs-th--tight" role="columnheader">Quantity used</div>
                <div className="modern-dsr-tech-th modern-dsr-tech-th--center modern-dsr-cs-th--tight" role="columnheader">Additional required</div>
              </div>

              {pagedRows.length === 0 ? (
                <div className="modern-dsr-tech-empty">No consumables lines loaded.</div>
              ) : (
                pagedRows.map((row) => (
                  <div
                    key={row.id}
                    className="modern-dsr-tech-row modern-dsr-cs-grid"
                    role="row"
                    onMouseEnter={() => setHoveredRowId(row.id)}
                    onMouseLeave={() => setHoveredRowId(null)}
                    data-active={hoveredRowId === row.id ? 'true' : undefined}
                  >
                    <div className="modern-dsr-tech-td modern-dsr-cs-td--readonly" role="cell" title={row.category}>
                      {row.category}
                    </div>
                    <div className="modern-dsr-tech-td modern-dsr-cs-td--readonly" role="cell" title={row.name}>
                      {row.name}
                    </div>
                    <div className="modern-dsr-tech-td modern-dsr-cs-td--readonly" role="cell" title={row.productCode}>
                      {row.productCode}
                    </div>
                    <div className="modern-dsr-tech-td modern-dsr-cs-td--num" role="cell">
                      {row.quantityAvailable}
                    </div>
                    <div className="modern-dsr-tech-td modern-dsr-cs-td--readonly modern-dsr-cs-td--muted" role="cell">
                      {row.unit}
                    </div>
                    <div
                      className="modern-dsr-tech-td modern-dsr-tech-td--num modern-dsr-cs-td--num modern-dsr-cs-td--qty"
                      role="cell"
                    >
                      {canEditQuantityUsed(row) ? (
                        <div className="modern-dsr-tech-hours-wrap">
                          <InputNumber
                            className="modern-dsr-tech-inputnum"
                            min={0}
                            value={row.quantityUsed}
                            onChange={(v) => handleQuantityUsedChange(row, v)}
                          />
                        </div>
                      ) : (
                        <span className="modern-dsr-cs-td--num-text">{row.quantityUsed}</span>
                      )}
                    </div>
                    <div
                      className="modern-dsr-tech-td modern-dsr-tech-td--num modern-dsr-cs-td--num modern-dsr-cs-td--qty"
                      role="cell"
                    >
                      {canEditAdditional() ? (
                        <div className="modern-dsr-tech-hours-wrap">
                          <InputNumber
                            className="modern-dsr-tech-inputnum"
                            min={0}
                            value={row.additionalRequired}
                            onChange={(v) => handleAdditionalRequiredChange(row, v)}
                          />
                        </div>
                      ) : (
                        <span className="modern-dsr-cs-td--num-text">{row.additionalRequired}</span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="modern-dsr-tech-log-pagination-bar">
              <Pagination
                className="heat-profile-pagination modern-dsr-tech-heat-profile-pagination"
                current={page}
                pageSize={pageSize}
                total={totalRows}
                showSizeChanger
                pageSizeOptions={['10', '20', '50', '100']}
                showTotal={(t, range) =>
                  t > 0 ? `${range[0]}-${range[1]} of ${t}` : `0-0 of ${t}`
                }
                onChange={(p, newSize) => {
                  setPaginationState((s) => {
                    if (newSize !== s.pageSize) {
                      return { current: 1, pageSize: newSize };
                    }
                    return { ...s, current: p };
                  });
                }}
              />
            </div>

            <div className="modern-dsr-tech-log-issues-block">
              <div className="modern-dsr-tech-log-issues-label">Comments</div>
              <TextArea
                className="modern-dsr-tech-log-issues-textarea"
                rows={3}
                placeholder="Please add your comments here"
                value={comments}
                onChange={handleCommentsChange}
                disabled={skipped}
              />
            </div>
          </div>
      </Spin>

      <div className="modern-dsr-tech-log-form-footer" aria-label="Form actions">
        <CrceButton label="Cancel" variant="tertiary" size="medium" onClick={onCancel} />
        <CrceButton
          label={
            isUpdatingConsumablesLog
              ? 'Saving…'
              : (isSubmitted ? 'Resubmit' : 'Save & Next')
          }
          variant="primary"
          size="medium"
          icon={Save}
          onClick={handleSave}
          disabled={isUpdatingConsumablesLog || isShiftSkipped}
        />
      </div>
    </div>
  );
};

const mapStateToProps = ({ dsrReducer }) => ({
  isConsumablesLogLoading: dsrReducer.isConsumablesLogLoading,
  isUpdatingConsumablesLog: dsrReducer.isUpdatingConsumablesLog,
  currentConsumablesLog: dsrReducer.currentConsumablesLog,
});

const mapDispatchToProps = (dispatch) => ({
  onFetchConsumablesLog: (payload) => dispatch(fetchConsumablesLog(payload)),
  onUpdateConsumablesLog: (payload) => dispatch(updateConsumablesLog(payload)),
  onUpdateDsrLogSections: (payload) => dispatch(updateDsrLogSections(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ConsumablesSection);
